import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Button,
  makeStyles,
} from "@material-ui/core";

import { Controller, useForm } from "react-hook-form";
import {
  RecipientPotentiallyUnknownOrg,
  RecipientsViewModel,
} from "../../../../../domain/RecipientsViewModel";
import { ManageRecipientsOrgs } from "./ManageRecipientOrgs";
import { Select } from "@ofqual/portal-components/components/Select";
import { rolesLookup, AoRoles } from "../../../../../domain/userRole";
import { useDispatch, useSelector } from "react-redux";
import { updatingRecipients } from "../../../../../redux/actions";
import { itemSelector } from "../../../../../redux/selectors";
import { InformationRequestStatus } from "../../../../../domain/InformationRequest";
import { useMemo, useState } from "react";
import { UpdatesWillBeCommitedDialog } from "../UpdatesWillBeCommitedDialog";
import {
  buildMetaData,
  getLookupTracker,
} from "@ofqual/portal-core/core/utils/modelHelpers";

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
}));

export const ManageRecipientsDialog: React.FC<{
  recipients: RecipientsViewModel<RecipientPotentiallyUnknownOrg>;
  onClose: () => void;
}> = ({ recipients, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { handleSubmit, control, watch, formState } = useForm<
    RecipientsViewModel<RecipientPotentiallyUnknownOrg>
  >({
    defaultValues: recipients,
    shouldUnregister: false,
  });

  const rolesLookups = rolesLookup(AoRoles);

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const submit = (
    model: RecipientsViewModel<RecipientPotentiallyUnknownOrg>
  ) => {
    const changedFields = Object.keys(
      dirtyFields
    ) as (keyof RecipientsViewModel)[];

    // create the meta pass it down for the audit trail
    const meta = buildMetaData(model, recipients, changedFields, lookupTracker);
    dispatch(updatingRecipients(recipients.id, model, meta));
    onClose();
  };

  const [openCommitDialog, setOpenCommitDialog] = useState(false);

  // check if there are any changes if so and the CAO is in the sent condition display a warning dialog where they can choose to review the changes.
  // if there are no changes just go...
  const onSubmit = () => {
    const changedFields = Object.keys(
      dirtyFields
    ) as (keyof RecipientsViewModel)[];
    if (changedFields.length > 0) {
      if (isSent) {
        setOpenCommitDialog(true);
      } else {
        handleSubmit(submit)();
      }
    } else {
      onClose();
    }
  };
  const handleCloseCommitDialog = (value: number) => {
    setOpenCommitDialog(false);
    if (value === 1) handleSubmit(submit)();
  };

  const roles = watch("roles");
  const haveRoles = !!roles.length;

  const itemState = useSelector(itemSelector);
  const isSent = itemState.item?.status === InformationRequestStatus.Sent;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="manage-recipients-dialog"
    >
      <DialogTitle data-test="manage-recipients-dialog-title">
        Manage Recipients
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="orgs"
                control={control}
                render={({ value, onChange }) => {
                  return (
                    <ManageRecipientsOrgs
                      orgs={value as RecipientPotentiallyUnknownOrg[]}
                      onChange={onChange}
                      haveRoles={haveRoles}
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="roles"
                control={control}
                as={
                  <Select
                    label="User role(s)"
                    selectVariant="multiple"
                    datatest="select-role-field"
                  >
                    {rolesLookups}
                  </Select>
                }
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          component="button"
          color="primary"
          onClick={onClose}
          data-test="close-manage-recipients-button"
        >
          Close
        </Button>
        <Button
          component="button"
          color="primary"
          onClick={onSubmit}
          data-test="save-manage-recipients-button"
        >
          Save
        </Button>
      </DialogActions>
      <UpdatesWillBeCommitedDialog
        open={openCommitDialog}
        onClose={handleCloseCommitDialog}
      />
    </Dialog>
  );
};
